import { useId, useState, type ReactNode } from 'react';

/**
 * Tiny inline-SVG charts for the console (ADR-0009: no charting dependency). One
 * measure per chart, one hue (`--accent`); identity comes from the title, not
 * colour, so no categorical palette is in play. Theme-aware through CSS tokens.
 */

export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'neutral' | 'good' | 'warn' | 'bad';
}): ReactNode {
  return (
    <div className="card card-pad stat-tile">
      <div className="stat-label">{label}</div>
      <div className={`stat-value ${tone !== 'neutral' ? tone : ''}`}>{value}</div>
      {hint ? <div className="stat-hint">{hint}</div> : null}
    </div>
  );
}

export interface BarPoint {
  label: string;
  value: number;
}

/**
 * A single-series column chart. Bars are anchored to the baseline with rounded
 * tops; hovering a column shows its exact value.
 */
export function BarSeries({
  data,
  height = 160,
  format = (n) => String(n),
}: {
  data: BarPoint[];
  height?: number;
  format?: (n: number) => string;
}): ReactNode {
  const clipId = useId();
  const [hover, setHover] = useState<number | null>(null);

  const max = Math.max(1, ...data.map((d) => d.value));
  const width = Math.max(data.length * 28, 280);
  const pad = { top: 8, bottom: 22, left: 4, right: 4 };
  const plotH = height - pad.top - pad.bottom;
  const slot = (width - pad.left - pad.right) / Math.max(1, data.length);
  const barW = Math.min(slot - 2, 40);

  return (
    <div className="bar-chart" style={{ overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={`Column chart, ${data.length} points, max ${format(max)}`}
      >
        <defs>
          <clipPath id={clipId}>
            <rect x="0" y="0" width={width} height={height} rx="4" />
          </clipPath>
        </defs>

        {/* baseline */}
        <line
          x1={pad.left}
          x2={width - pad.right}
          y1={pad.top + plotH}
          y2={pad.top + plotH}
          stroke="var(--border-strong)"
          strokeWidth="1"
        />

        {data.map((d, i) => {
          const h = max > 0 ? (d.value / max) * plotH : 0;
          const x = pad.left + i * slot + (slot - barW) / 2;
          const y = pad.top + plotH - h;
          const active = hover === i;
          return (
            <g
              key={d.label}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            >
              {/* full-height hit target */}
              <rect x={pad.left + i * slot} y={pad.top} width={slot} height={plotH} fill="transparent" />
              <rect
                x={x}
                y={y}
                width={barW}
                height={Math.max(h, d.value > 0 ? 2 : 0)}
                rx="4"
                fill="var(--accent)"
                opacity={hover === null || active ? 1 : 0.55}
              >
                <title>
                  {d.label}: {format(d.value)}
                </title>
              </rect>
              {active ? (
                <text
                  x={x + barW / 2}
                  y={y - 4}
                  textAnchor="middle"
                  fontSize="10"
                  fill="var(--text)"
                >
                  {format(d.value)}
                </text>
              ) : null}
              {i % Math.ceil(data.length / 8 || 1) === 0 ? (
                <text
                  x={x + barW / 2}
                  y={height - 6}
                  textAnchor="middle"
                  fontSize="10"
                  fill="var(--text-faint)"
                >
                  {d.label}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Horizontal proportion bar, e.g. completion rate. */
export function RateBar({ value }: { value: number }): ReactNode {
  const pct = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <div className="rate-bar" title={`${pct}%`}>
      <div className="rate-bar-fill" style={{ width: `${pct}%` }} />
      <span className="rate-bar-label">{pct}%</span>
    </div>
  );
}
